///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2021 - 2025
//                           RXO Inc.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of RXO Inc. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import { createSlice } from "@reduxjs/toolkit";

import {
  deleteLane,
  deleteLanes,
  getIsLaneUploadInProgress,
  getPricingLanes,
  getRxoTruckloadLanePricingFilterFacets,
  searchRxoTruckloadLanesForPricingPaged,
  patchTruckloadEditedlanes,
  getTruckloadLaneHasMatches,
  getSpecificLanes,
  patchEuTruckloadEditedlanes,
  getIsLanePriceInProgress
} from "../api/lanes";
import getPricingGridColumns from "../helpers/getPricingGridColumns";
import getUserLaneType from "../helpers/getUserLaneType";
import handlePricingCellChange from "../helpers/handlePricingCellChange";
import pricingSliceHelper from "../helpers/pricingSliceHelper";
import { MARGIN, MARGIN_PERCENTAGE } from "../helpers/tlColumns";
import tlColumnConstants from "../helpers/tlColumnConstants";
import {
  controls,
  division,
  laneType,
  order,
  repriceReason,
  userRole
} from "../helpers/types";
import updatePricingGridDetails from "../helpers/updatePricingGridDetails";
import {
  fetchBidSummary,
  receiveDeleteItemFailure,
  requestDeleteItem,
  resetDeleteItemState
} from "./bidSummarySlice";
import {
  fetchMatchingLanes,
  resetLaneMatchState,
  toggleDisableLane as toggleDisableLaneMatch,
  fetchDisabledMatchingLanes
} from "./laneMatchSlice";
import bindEditedLanes from "../helpers/bindEditedLanes";
import handleSpecialPastingInGrid from "../helpers/handleSpecialPastingInGrid";
import { fetchLoadHistory } from "./analyticsSlice";
import { manualRepriceForBid } from "../api/bids";
import getDefaultLaneMatchFilters from "../helpers/getDefaultLaneMatchFilters";
import {
  buildRxoTlPricingSearchRequest,
  getEffectiveRoleForLanes,
  shouldUseRxoTlServerPaging
} from "../helpers/rxoTlPricingPagedSearch";

export const initialState = {
  lanes: null,
  lanesLoading: false,
  lanesError: false,
  totalLanes: null,
  averageMargin: null,
  isCoyoteBid: undefined,
  sortField: null,
  sortOrder: order.ascending,
  searchTerm: "",
  offset: 0,
  perPage: 50,
  totalFiltered: 0,
  columnFilters: {},
  rangeFilters: {},
  columnFilterMenu: { field: null, visible: false },
  filtersApplied: false,
  saveLaneEditsLoading: false,
  saveLaneEditsFailure: false,
  columns: {
    changed: false,
    order: []
  },
  columnWidths: {},
  isLaneImportInProgress: false,
  importInProgressModal: false,
  pricingProgress: {
    isLaneRepriceInProgress: false,
    repriceReason: repriceReason.none
  },
  repriceInProgressModal: false,
  manualReprice: {
    isLoading: false,
    isFailure: false
  },
  rxoPricingFilterFacets: null,
  useRxoTlServerPagedLanes: false
};

const bidPricingSlice = createSlice({
  name: "bidPricing",
  initialState,
  reducers: {
    requestPricingLanes: (state, action) =>
      pricingSliceHelper.requestPricingLanes(state, action, initialState),
    requestRxoTlPricingSearchStart:
      pricingSliceHelper.requestRxoTlPricingSearchStart,
    receivePricingLanesSuccess: (state, action) =>
      pricingSliceHelper.receivePricingLanesSuccess(
        state,
        action,
        initialState
      ),
    receivePricingLanesFailure: state =>
      pricingSliceHelper.receivePricingLanesFailure(state),
    filterPricingLanes: (state, action) => {
      pricingSliceHelper.filterPricingLanes(state, action);
      updatePricingGridDetails(state);
    },
    updateServerPagedPricingFilters:
      pricingSliceHelper.updateServerPagedPricingFilters,
    resetRxoTlPricingGridFilters: state =>
      pricingSliceHelper.resetRxoTlPricingGridFilters(state, initialState),
    receiveRxoTlPricingFilterFacets: (state, { payload }) => {
      state.rxoPricingFilterFacets = payload;
    },
    receiveRxoTlPagedLanesSuccess: (state, action) =>
      pricingSliceHelper.receiveRxoTlPagedLanesSuccess(
        state,
        action,
        initialState
      ),
    setSortStateOnly: pricingSliceHelper.setSortStateOnly,
    sortPricingLanes: pricingSliceHelper.sortPricingLanes,
    handleCellChange: handlePricingCellChange,
    changePricingLanesPagination: (state, { payload: { offset, limit } }) => {
      state.offset = offset;
      state.perPage = limit;
    },
    requestSaveLaneEdits: pricingSliceHelper.requestSaveLaneEdits,
    saveLaneEditsFailure: pricingSliceHelper.saveLaneEditsFailure,
    saveLaneEditsReset: pricingSliceHelper.saveLaneEditsReset,
    toggleColumnFilterMenu: pricingSliceHelper.toggleColumnFilterMenu,
    toggleRxoTlColumnFilterMenu: pricingSliceHelper.toggleRxoTlColumnFilterMenu,
    resetPricingGridFilters: state => {
      pricingSliceHelper.resetPricingGridFilters(state, initialState);
      updatePricingGridDetails(state);
    },
    loadColumnOrder: (state, { payload: { role, forceLoad, isCoyoteBid } }) => {
      const columns = getPricingGridColumns(role, state.division, null, isCoyoteBid)
        .filter(c => !c.onlyLaneMatch && c.control !== controls.hide)
        .map(c => c.field);

      pricingSliceHelper.loadColumns(
        state,
        columns,
        localStorage.columnOrder,
        forceLoad
      );
    },
    setColumnOrder: (state, action) => {
      pricingSliceHelper.setColumnOrder(
        state,
        action,
        getPricingGridColumns(action.payload.role, state.division, null, state.isCoyoteBid).map(
          c => c.field
        )
      );
      localStorage.columnOrder = JSON.stringify(state.columns.order);
    },
    resetColumnOrder: ({ columns, division }, { payload }) => {
      columns.order = getPricingGridColumns(payload.role, division, null, payload.isCoyoteBid)
        .filter(c => !c.onlyLaneMatch && c.control !== controls.hide)
        .map(c => c.field);
      columns.changed = false;
      localStorage.removeItem("columnOrder");
    },
    applyMassMargin: (state, { payload: { massMarginFields, role } }) => {
      const marginDollarField = state.isCoyoteBid
        ? MARGIN
        : tlColumnConstants.PWB_MARGIN;
      state.lanes
        .filter(lane => lane.metaData.matched)
        .forEach(lane => {
          handlePricingCellChange(state, {
            payload: {
              index: lane.metaData.defaultIndex,
              field: MARGIN_PERCENTAGE,
              value: massMarginFields.flatMarginPct,
              role
            }
          });

          if (
            lane[marginDollarField] < massMarginFields.minMargin ||
            lane[marginDollarField] > massMarginFields.maxMargin
          ) {
            handlePricingCellChange(state, {
              payload: {
                index: lane.metaData.defaultIndex,
                field: marginDollarField,
                value:
                  lane[marginDollarField] < massMarginFields.minMargin
                    ? massMarginFields.minMargin
                    : massMarginFields.maxMargin,
                role
              }
            });
          }
        });
    },
    setColumnWidth: pricingSliceHelper.setColumnWidth,
    setLaneImportInProgress: (state, { payload }) => {
      state.isLaneImportInProgress = payload;
    },
    showImportInProgressModal: (state, { payload }) => {
      state.importInProgressModal = payload;
    },
    requestLaneMatchCount: (state, { payload }) => {
      const { lanes, offset, perPage, useRxoTlServerPagedLanes } = state;
      const matched = lanes?.filter(lane => lane.metaData.matched) ?? [];
      const pageLanes = useRxoTlServerPagedLanes
        ? matched
        : matched.slice(offset, offset + perPage);
      pageLanes.map(l => {
          l.isFetchingLaneCount = payload.find(x => x === l.truckloadLaneId)
            ? true
            : false;
          l.isErrorLaneCount = false;
          return l;
        });
    },
    receiveLaneMatchCountSuccess: (state, { payload }) => {
      const { lanes, offset, perPage, useRxoTlServerPagedLanes } = state;
      const matched = lanes?.filter(lane => lane.metaData.matched) ?? [];
      const pageLanes = useRxoTlServerPagedLanes
        ? matched
        : matched.slice(offset, offset + perPage);
      pageLanes.map(l => {
          let laneHasMatches = payload.find(
            lane => lane.truckloadLaneId === l.truckloadLaneId
          );
          l.hasMatches = laneHasMatches
            ? laneHasMatches.hasMatches
            : undefined;
          l.isFetchingLaneCount = false;
          l.isErrorLaneCount = false;
          return l;
        });
    },
    receiveLaneMatchCountFailure: (state, { payload }) => {
      const { lanes, offset, perPage, useRxoTlServerPagedLanes } = state;
      const matched = lanes?.filter(lane => lane.metaData.matched) ?? [];
      const pageLanes = useRxoTlServerPagedLanes
        ? matched
        : matched.slice(offset, offset + perPage);
      pageLanes.map(l => {
          l.isFetchingLaneCount = false;
          l.isErrorLaneCount = payload.find(x => x === l.truckloadLaneId)
            ? true
            : false;
          return l;
        });
    },
    handleSpecialPaste: handleSpecialPastingInGrid,
    toggleDisableLane: pricingSliceHelper.toggleDisableLane,
    replaceDisabledLanes: (state, action) => {
      pricingSliceHelper.replaceDisabledLanes(state, action);
      if (state.lanes?.length > 0) {
        let sum = 0;
        state.lanes.forEach(l => (sum += l.metaData.originaLane.margin));
        state.averageMargin =
          Math.round((sum / state.lanes.length) * 100) / 100;
      }
    },
    requestManualReprice: state => {
      state.manualReprice.isLoading = true;
      state.manualReprice.isFailure = false;
    },
    resetManualReprice: state => {
      state.manualReprice = initialState.manualReprice;
    },
    receiveManualRepriceFailure: state => {
      state.manualReprice.isLoading = false;
      state.manualReprice.isFailure = true;
    },
    setLaneRepriceInProgress: (state, { payload }) => {
      state.pricingProgress.isLaneRepriceInProgress =
        payload.isLanePricingInProgress;
      state.pricingProgress.repriceReason = payload.repriceReason;
    },
    showRepriceInProgressModal: (state, { payload }) => {
      state.repriceInProgressModal = payload;
    },
    setTableColumns: (state, { payload: { columns, role, division } }) => {
      state.columns.order = columns;
      localStorage.columnOrder = JSON.stringify(columns);
      const defaultColumns = getPricingGridColumns(role, division, null, state.isCoyoteBid)
        .filter(c => !c.onlyLaneMatch && c.control !== controls.hide)
        .map(c => c.field);
      state.columns.changed = defaultColumns !== columns;
    },
    reset: () => initialState
  }
});

export const {
  requestPricingLanes,
  requestRxoTlPricingSearchStart,
  receivePricingLanesSuccess,
  receivePricingLanesFailure,
  filterPricingLanes,
  updateServerPagedPricingFilters,
  resetRxoTlPricingGridFilters,
  receiveRxoTlPricingFilterFacets,
  receiveRxoTlPagedLanesSuccess,
  setSortStateOnly,
  sortPricingLanes,
  handleCellChange,
  changePricingLanesPagination,
  requestSaveLaneEdits,
  saveLaneEditsFailure,
  saveLaneEditsReset,
  toggleColumnFilterMenu,
  toggleRxoTlColumnFilterMenu,
  resetPricingGridFilters,
  loadColumnOrder,
  setColumnOrder,
  resetColumnOrder,
  applyMassMargin,
  setColumnWidth,
  setLaneImportInProgress,
  showImportInProgressModal,
  requestLaneMatchCount,
  receiveLaneMatchCountSuccess,
  receiveLaneMatchCountFailure,
  handleSpecialPaste,
  toggleDisableLane,
  replaceDisabledLanes,
  requestManualReprice,
  resetManualReprice,
  receiveManualRepriceFailure,
  setLaneRepriceInProgress,
  showRepriceInProgressModal,
  setTableColumns,
  reset
} = bidPricingSlice.actions;

export const bidPricingReducer = bidPricingSlice.reducer;

export const setLoading =
  ({
    role,
    division,
    clearFilterAndSortDetails,
    shouldFetchMatchingLanes,
    isCoyoteBid
  }) =>
  (dispatch, getState) => {
    const previousDivision = getState().bidPricing.division;

    dispatch(requestPricingLanes({ clearFilterAndSortDetails, division, isCoyoteBid }));
    dispatch(
      loadColumnOrder({
        role,
        forceLoad: previousDivision !== division,
        isCoyoteBid
      })
    );
    shouldFetchMatchingLanes && dispatch(resetLaneMatchState());
  };

export const fetchRxoTlPricingLanes =
  ({
    bidId,
    role,
    clearFilterAndSortDetails = false,
    shouldFetchMatchingLanes = true,
    softRefresh = false,
    loadFacets = false
  }) =>
  async (dispatch, getState) => {
    const {
      bidPricing,
      user: { user },
      bidSummary: { summary }
    } = getState();

    const effectiveRole =
      role === userRole.management ? user.userRoleForManagementUser : role;
    const requestedLaneType = getUserLaneType(effectiveRole);

    if (!softRefresh) {
      dispatch(
        setLoading({
          role: effectiveRole,
          division: bidPricing.division,
          clearFilterAndSortDetails,
          shouldFetchMatchingLanes,
          isCoyoteBid: summary?.isCoyoteBid
        })
      );
    } else {
      dispatch(requestRxoTlPricingSearchStart());
    }

    try {
      if (loadFacets) {
        const { data: facets } = await getRxoTruckloadLanePricingFilterFacets(
          bidId,
          user.userRoleForManagementUser
        );
        dispatch(receiveRxoTlPricingFilterFacets(facets));
      }

      const p = getState().bidPricing;
      const body = buildRxoTlPricingSearchRequest({
        bidId,
        userRoleForLanesWhenManagementUser: getEffectiveRoleForLanes(role, user),
        offset: p.offset,
        perPage: p.perPage,
        sortField: p.sortField,
        sortOrder: p.sortOrder,
        columnFilters: p.columnFilters,
        rangeFilters: p.rangeFilters,
        searchTerm: p.searchTerm
      });

      const { data } = await searchRxoTruckloadLanesForPricingPaged(body);

      dispatch(
        receiveRxoTlPagedLanesSuccess({
          lanesResponse: {
            lanes: data.lanes || [],
            totalCount: data.totalCount,
            averageMargin: data.averageMargin
          },
          requestedLaneType
        })
      );

      if (
        (requestedLaneType === laneType.standardTruckload ||
          requestedLaneType === laneType.euTruckload) &&
        data.lanes?.length
      ) {
        dispatch(getLaneMatchCountForTLLanes(true));
        if (shouldFetchMatchingLanes) {
          dispatch(
            fetchMatchingLanes({
              role: effectiveRole,
              truckloadLane: data.lanes[0]
            })
          );
          dispatch(
            fetchLoadHistory({
              division: bidPricing.division,
              lane: data.lanes[0]
            })
          );
        }
      }
    } catch {
      dispatch(receivePricingLanesFailure());
    }
  };

export const refetchRxoTlPricingPage =
  ({ bidId, role, shouldFetchMatchingLanes = false, loadFacets = false }) =>
  dispatch =>
    dispatch(
      fetchRxoTlPricingLanes({
        bidId,
        role,
        clearFilterAndSortDetails: false,
        shouldFetchMatchingLanes,
        softRefresh: true,
        loadFacets
      })
    );

export const applyServerPagedPricingFiltersAndRefetch =
  ({ bidId, role, ...payload }) =>
  async dispatch => {
    dispatch(updateServerPagedPricingFilters(payload));
    await dispatch(
      refetchRxoTlPricingPage({ bidId, role, shouldFetchMatchingLanes: false })
    );
  };

export const sortRxoTlPricingLanesAndRefetch =
  ({ bidId, role, field }) =>
  async dispatch => {
    dispatch(setSortStateOnly({ field }));
    await dispatch(
      refetchRxoTlPricingPage({ bidId, role, shouldFetchMatchingLanes: false })
    );
  };

export const setServerPagedSearchAndRefetch =
  ({ bidId, role, searchTerm }) =>
  async dispatch => {
    dispatch(updateServerPagedPricingFilters({ searchTerm }));
    await dispatch(
      refetchRxoTlPricingPage({ bidId, role, shouldFetchMatchingLanes: false })
    );
  };

export const resetRxoTlPricingFiltersAndRefetch =
  ({ bidId, role }) =>
  async dispatch => {
    dispatch(resetRxoTlPricingGridFilters());
    await dispatch(
      fetchRxoTlPricingLanes({
        bidId,
        role,
        clearFilterAndSortDetails: false,
        shouldFetchMatchingLanes: true,
        softRefresh: false,
        loadFacets: true
      })
    );
  };

export const fetchPricingLanes =
  ({
    bidId,
    role,
    clearFilterAndSortDetails = false,
    shouldFetchMatchingLanes = true,
    bidDivision,
    isCoyoteBid,
    loadRxoTlPricingFilterFacets = false
  }) =>
  (dispatch, getState) => {
    const {
      bidPricing: { division: storeDivision },
      bidSummary: { summary },
      user: { user },
      featureFlags
    } = getState();
    bidDivision = bidDivision ? bidDivision : storeDivision;
    isCoyoteBid =
      isCoyoteBid !== undefined ? isCoyoteBid : summary?.isCoyoteBid;

    if (
      shouldUseRxoTlServerPaging({
        enableRxoTlPricingServerPagination:
          featureFlags.enableRxoTlPricingServerPagination,
        isCoyoteBid,
        bidDivision,
        effectiveRoleForLanes: getEffectiveRoleForLanes(role, user)
      })
    ) {
      return dispatch(
        fetchRxoTlPricingLanes({
          bidId,
          role,
          clearFilterAndSortDetails,
          shouldFetchMatchingLanes,
          softRefresh: false,
          loadFacets:
            clearFilterAndSortDetails || loadRxoTlPricingFilterFacets
        })
      );
    }

    const requestedLaneType = getUserLaneType(
      role === userRole.management ? user.userRoleForManagementUser : role,
      bidDivision
    );
    dispatch(
      setLoading({
        role:
          role === userRole.management ? user.userRoleForManagementUser : role,
        division: bidDivision,
        clearFilterAndSortDetails,
        shouldFetchMatchingLanes,
        isCoyoteBid
      })
    );
    getPricingLanes(bidId, user.userRoleForManagementUser)
      .then(({ data }) => {
        if (bidDivision === division.eu) {
          data.lanes = data.lanes.map(l => ({ ...l, truckloadLaneId: l.id }));
        }
        dispatch(
          receivePricingLanesSuccess({
            lanesResponse: data,
            requestedLaneType,
            role:
              role === userRole.management
                ? user.userRoleForManagementUser
                : role,
            clearFilterAndSortDetails
          })
        );
        if (clearFilterAndSortDetails === false) {
          dispatch(
            filterPricingLanes({
              applyOnExistingFilters: true,
              role:
                role === userRole.management
                  ? user.userRoleForManagementUser
                  : role
            })
          );
          dispatch(sortPricingLanes({ field: "" }));
        }
        if (
          (requestedLaneType === laneType.standardTruckload ||
            requestedLaneType === laneType.euTruckload) &&
          data.lanes.length
        ) {
          dispatch(getLaneMatchCountForTLLanes(true));
          if (shouldFetchMatchingLanes) {
            dispatch(
              fetchMatchingLanes({
                role:
                  role === userRole.management
                    ? user.userRoleForManagementUser
                    : role,
                truckloadLane: data.lanes[0]
              })
            );
            dispatch(
              fetchLoadHistory({
                division: bidDivision,
                lane: data.lanes[0]
              })
            );
          }
        }
      })
      .catch(() => dispatch(receivePricingLanesFailure()));
  };

export const saveEditedLane = (bidId, role) => (dispatch, getState) => {
  dispatch(requestSaveLaneEdits());
  const { bidPricing } = getState();
  const requestLaneEdits = bindEditedLanes(bidPricing);
  (bidPricing.division === division.eu
    ? patchEuTruckloadEditedlanes(bidId, requestLaneEdits)
    : patchTruckloadEditedlanes(bidId, requestLaneEdits)
  )
    .then(() => {
      dispatch(fetchDisabledMatchingLanes());
      dispatch(
        fetchBidSummary({
          bidId,
          role,
          fetchTLLaneCount: false,
          division: bidPricing.division,
          fetchLanes: true,
          shouldFetchMatchingLanes: false,
          clearFilterAndSortDetails: false,
          loadRxoTlPricingFilterFacets: true
        })
      );
    })
    .catch(() => {
      dispatch(saveLaneEditsFailure());
    });
};

export const deleteSingleLane =
  (bidId, laneId, role) => async (dispatch, getState) => {
    const totalLane = getState().bidPricing.totalLanes;
    const { user } = getState().user;
    const { summary } = getState().bidSummary;

    dispatch(requestDeleteItem());
    try {
      const request = { bidId: parseInt(bidId), laneId: parseInt(laneId) };
      await deleteLane(request, user.userRoleForManagementUser);
      dispatch(resetDeleteItemState());
      totalLane === 1 && dispatch(fetchBidSummary({ bidId, role }));
      dispatch(
        fetchPricingLanes({ bidId, role, isCoyoteBid: summary?.isCoyoteBid })
      );
    } catch {
      dispatch(receiveDeleteItemFailure());
    }
  };

export const deleteAllLanes = (bidId, role) => async (dispatch, getState) => {
  dispatch(requestDeleteItem());
  const { user } = getState().user;
  const { summary } = getState().bidSummary;

  try {
    const request = { bidId: parseInt(bidId) };
    await deleteLanes(request, user.userRoleForManagementUser);
    dispatch(resetDeleteItemState());
    dispatch(fetchBidSummary({ bidId, role }));
    dispatch(
      fetchPricingLanes({ bidId, role, isCoyoteBid: summary?.isCoyoteBid })
    );
  } catch {
    dispatch(receiveDeleteItemFailure());
  }
};

export const checkLaneImportInProgress =
  (bidId, callbackSuccess, callbackError) => async (dispatch, getState) => {
    const { user } = getState().user;
    try {
      let value = (
        await getIsLaneUploadInProgress(bidId, user.userRoleForManagementUser)
      ).data;
      dispatch(setLaneImportInProgress(value));
      callbackSuccess && callbackSuccess(value);
    } catch {
      dispatch(setLaneImportInProgress(false));
      callbackError && callbackError();
    }
  };

export const getLaneMatchCountForTLLanes =
  (useDefaultLaneMatchFilters = false) =>
  async (dispatch, getState) => {
    const { lanes, offset, perPage, useRxoTlServerPagedLanes } =
      getState().bidPricing;
    const { filters } = getState().laneMatch;

    const matched = lanes?.filter(lane => lane.metaData.matched) ?? [];
    const pageLanes = useRxoTlServerPagedLanes
      ? matched
      : matched.slice(offset, offset + perPage);

    const truckloadLaneIds = pageLanes.map(l => l.truckloadLaneId);

    if (truckloadLaneIds?.length) {
      dispatch(requestLaneMatchCount(truckloadLaneIds));

      try {
        let defaultFilters = null;
        if (useDefaultLaneMatchFilters) {
          defaultFilters = getDefaultLaneMatchFilters();
        }
        const laneHasMatches = (
          await getTruckloadLaneHasMatches(
            truckloadLaneIds,
            getState().bidSummary.summary?.bidId,
            useDefaultLaneMatchFilters ? defaultFilters : filters
          )
        ).data;

        dispatch(receiveLaneMatchCountSuccess(laneHasMatches));
      } catch {
        dispatch(receiveLaneMatchCountFailure(truckloadLaneIds));
      }
    }
  };

export const changeCellAndDisableIfRequiredPricing =
  payload => async (dispatch, getState) => {
    dispatch(handleCellChange(payload));
    const { laneMatch, bidPricing } = getState();
    const { truckloadLaneId } = bidPricing.lanes[payload.index];

    if (laneMatch.lanes.some(l => l.truckloadLaneId === truckloadLaneId)) {
      dispatch(
        toggleDisableLaneMatch([
          {
            truckloadLaneId,
            disabled: bidPricing.lanes[payload.index].metaData.laneChanged
          }
        ])
      );
    }
  };

export const fetchDisabledPricingLanes = () => async (dispatch, getState) => {
  const {
    bidPricing: { lanes },
    bidSummary: { summary }
  } = getState();
  const disabledLaneIds = lanes
    ?.filter(l => l.metaData.disabled)
    .map(l => l.truckloadLaneId);
  if (disabledLaneIds?.length > 0) {
    try {
      const specificLanesResponse = await getSpecificLanes(
        summary?.bidId,
        disabledLaneIds
      );
      if (summary?.division === division.eu) {
        specificLanesResponse.data = specificLanesResponse.data.map(l => ({
          ...l,
          truckloadLaneId: l.id
        }));
      }
      dispatch(
        replaceDisabledLanes({ updatedLanes: specificLanesResponse.data })
      );
    } catch {
      dispatch(receivePricingLanesFailure());
    }
  }
};

export const checkLaneRepriceInProgress =
  (bidId, callbackSuccess, callbackError) => async (dispatch, getState) => {
    const { user } = getState().user;
    try {
      let value = (
        await getIsLanePriceInProgress(bidId, user.userRoleForManagementUser)
      ).data;
      dispatch(setLaneRepriceInProgress(value));
      callbackSuccess && callbackSuccess(value.isLanePricingInProgress);
    } catch {
      const defaultValue = {
        isLanePricingInProgress: false,
        repriceReason: repriceReason.none
      };
      dispatch(setLaneRepriceInProgress(defaultValue));
      callbackError && callbackError();
    }
  };

export const initiateManualReprice =
  (bidId, role, division) => async dispatch => {
    dispatch(requestManualReprice());

    try {
      await manualRepriceForBid(bidId);
      dispatch(resetManualReprice());
      dispatch(checkLaneRepriceInProgress(bidId));
      dispatch(
        fetchBidSummary({
          bidId,
          role,
          fetchTLLaneCount: false,
          division,
          fetchLanes: false,
          shouldFetchMatchingLanes: false
        })
      );
    } catch {
      dispatch(receiveManualRepriceFailure());
    }
  };
